import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PD_STEPS, PLANNING_STEPS, PHASE_OF, STARTER_CONFIG } from "./quote/constants";
import { StepTracker } from "./quote/ui";
import { PromoCountdown, PostcodeStep, RouteStep, CriteriaFailModal } from "./quote/steps";
import { PDScreen } from "./quote/PDScreen";
import { SiteStep } from "./quote/SiteStep";
import { PlanningQuoteStep } from "./quote/PlanningQuoteStep";
import { ConfiguratorStep } from "./quote/ConfiguratorStep";
import { QuoteRevealStep } from "./quote/QuoteRevealStep";
import { BookStep } from "./quote/BookStep";
import { StaffAdminNote, readAdminNoteDraft, clearAdminNoteDraft } from "./quote/StaffAdminNote";
import { useSeo } from "../hooks/useSeo";

export default function Quote() {
  const nav = useNavigate();
  useSeo({
    title: "Instant Extension Quote Tool — Design & Price Your Rear Extension Online | The Extension Works",
    description: "Use our free instant quote tool to design your single-storey rear extension in 3D, get a fixed VAT-inclusive price in minutes and book your start date online. No sales visits.",
    canonical: "https://theextensionworks.co.uk/quote",
    image: "https://theextensionworks.co.uk/brand/og-image.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to get an instant rear extension quote online",
      "description": "Design, price and book a single-storey rear extension entirely online with The Extension Works.",
      "step": [
        { "@type": "HowToStep", "position": 1, "name": "Postcode & route", "text": "Enter your postcode and tell us whether your project is Permitted Development or already has planning permission." },
        { "@type": "HowToStep", "position": 2, "name": "Configure in 3D", "text": "Set the width, depth, roof, doors, windows and finishes and watch your extension modelled live in 3D." },
        { "@type": "HowToStep", "position": 3, "name": "See your fixed price", "text": "Reveal your itemised, VAT-inclusive fixed price and AI visuals, saved under your own quote reference." },
        { "@type": "HowToStep", "position": 4, "name": "Book online", "text": "Choose a start date and pay a 10% deposit to lock your slot." }
      ]
    }
  });
  const [stepIdx, setStepIdx] = useState(0);
  const [pubSettings, setPubSettings] = useState(null);
  const [addons, setAddons] = useState([]);
  const [postcode, setPostcode] = useState("");
  const [serviceable, setServiceable] = useState(null);
  const [route, setRoute] = useState(null); // 'pd' | 'planning'
  const [isStaff, setIsStaff] = useState(false); // signed-in admin/office → skip postcode + contact gate
  const [adminNote, setAdminNote] = useState(""); // private staff note draft (auto-saved to localStorage)
  const steps = route === "planning" ? PLANNING_STEPS : PD_STEPS;
  const step = steps[stepIdx];
  const [planningRef, setPlanningRef] = useState("");
  const [planningQuoteId, setPlanningQuoteId] = useState(null);
  const [planningContact, setPlanningContact] = useState({ name: "", email: "" });
  const [pd, setPd] = useState({
    // sensible defaults — most properties pass these; user only flips the exceptions
    flat: "no", listed: "no", conservation: "no", article4: "no", changeofuse: "no", coverage: "yes",
  });
  const [priorApproved, setPriorApproved] = useState(""); // "" | "yes" | "no" — for depths beyond the PD limit
  const [propType, setPropType] = useState("detached");
  const [pdDepth, setPdDepth] = useState(3);
  const [site, setSite] = useState({ ulez: true, ulez_days: 70, rear_access: "wide", no_driveway: false, demolish: false, demolish_sqm: 0, steel_beam: false, build_over: false, living_during: true, parking_3vans: true, permit_parking: false });
  const [config, setConfig] = useState(STARTER_CONFIG);
  const [quoteResult, setQuoteResult] = useState(null);
  const [pdQuote, setPdQuote] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [enquiryId, setEnquiryId] = useState(null);
  const [linkBooking, setLinkBooking] = useState(null);

  // Track the quote journey once contact is captured — drop-offs surface as
  // "Abandoned quote" enquiries for the office to chase (process_abandoned_leads).
  const leadIdRef = useRef(null);
  const upsertLead = (stepName) => {
    if (pdQuote?.quote_id || editingQuoteId || enquiryId || linkBooking) return; // saved already / staff flows
    if (!contact.name.trim() || !contact.email.trim() || !contact.phone.trim()) return;
    api.post("/quote/lead", {
      lead_id: leadIdRef.current || undefined,
      name: contact.name, email: contact.email, phone: contact.phone,
      postcode, route: route || "", step: stepName,
      config: { width: config.width, depth: config.depth },
    }).then(r => { leadIdRef.current = r.data.lead_id; }).catch(() => {});
  };
  useEffect(() => { upsertLead(step); }, [stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { api.get("/public/settings").then(r => setPubSettings(r.data)); }, []);
  useEffect(() => { api.get("/public/addons").then(r => setAddons(r.data.items || [])).catch(() => {}); }, []);

  // Staff/admin bypass: if the visitor is signed into the admin/office portal, skip the postcode
  // and contact gate — the office already has (or will type in) the client's details themselves.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (r?.data?.email) {
          setIsStaff(true);
          setAdminNote(readAdminNoteDraft()); // restore any half-typed note from earlier
          // Only auto-advance if we're still on the postcode step and no deep-link is in play
          const p = new URLSearchParams(window.location.search);
          const deepLink = p.get("edit") || p.get("d") || p.get("resume") || p.get("enquiry");
          if (!deepLink) {
            setServiceable(true); // silences the postcode UI logic downstream
            setStepIdx(i => (i === 0 ? PD_STEPS.indexOf("route") : i));
          }
        }
      } catch { /* not signed in — normal public flow */ }
    })();
  }, []);

  // Re-open a saved PD quote from ?edit=<quote_id> pre-filled, under the SAME reference
  useEffect(() => {
    const eid = new URLSearchParams(window.location.search).get("edit");
    if (!eid) return;
    (async () => {
      try {
        const r = await api.get(`/quote/pd/${eid}`);
        const d = r.data || {};
        setRoute("pd");
        if (d.config) setConfig(c => ({ ...STARTER_CONFIG, ...d.config }));
        if (d.site) setSite(s => ({ ...s, ...d.site }));
        if (d.postcode) setPostcode(d.postcode);
        setContact({ name: d.name || "", email: d.email || "", phone: d.phone || "" });
        setEditingQuoteId(eid);
        setPdQuote({ quote_id: eid, reference: d.reference, photo_url: d.photo_url, total_inc_vat: d.total_inc_vat, valid_until: null, spec_lines: [] });
        setStepIdx(PD_STEPS.indexOf("configurator"));
        toast.success(`Editing quote ${d.reference} — change anything and re-save under the same reference.`);
      } catch { toast.error("That quote link is invalid or has expired."); }
    })();
  }, []);

  // Staff shortcut: prefill from an online enquiry (?postcode&name&email&phone&enquiry)
  // so the office can build a quote for any enquirer — incl. out-of-area postcodes.
  // The optional ?link_booking=<ref> query param (used by the Job File "Build in
  // configurator (as customer)" button) auto-links the resulting quote back to the
  // job on save so both records stay in sync.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("link_booking")) setLinkBooking(p.get("link_booking"));
    if (p.get("edit") || p.get("d")) return;
    if (p.get("postcode")) setPostcode(p.get("postcode"));
    if (p.get("name") || p.get("email") || p.get("phone")) {
      setContact({ name: p.get("name") || "", email: p.get("email") || "", phone: p.get("phone") || "" });
    }
    if (p.get("enquiry")) {
      setEnquiryId(p.get("enquiry"));
      toast.info("Quoting for an enquiry — their details are prefilled and the enquiry will be marked as quoted on save.");
    }
    if (p.get("link_booking")) {
      toast.info(`Building this quote for job ${p.get("link_booking")} — it will be auto-linked to the job file on save.`);
    }
  }, []);

  // Load a shared design from ?d=<id> and jump straight to the configurator
  useEffect(() => {
    const did = new URLSearchParams(window.location.search).get("d");
    if (!did) return;
    (async () => {
      try {
        const r = await api.get(`/quote/share/${did}`);
        const d = r.data?.data || {};
        if (d.route) setRoute(d.route);
        if (d.propType) setPropType(d.propType);
        if (typeof d.pdDepth === "number") setPdDepth(d.pdDepth);
        if (d.site) setSite(s => ({ ...s, ...d.site }));
        if (d.config) setConfig(c => ({ ...STARTER_CONFIG, ...d.config }));
        setStepIdx(PD_STEPS.indexOf("configurator"));
        toast.success("Loaded a shared design — tweak anything you like.");
      } catch { toast.error("That shared design link is invalid or has expired."); }
    })();
  }, []);

  // Resume an abandoned quote journey from the recovery email (?resume=<lead_id>)
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get("resume");
    if (!rid) return;
    (async () => {
      try {
        const r = await api.get(`/quote/lead/${rid}`);
        const d = r.data || {};
        setContact({ name: d.name || "", email: d.email || "", phone: d.phone || "" });
        if (d.postcode) setPostcode(d.postcode);
        if (d.route === "pd" || d.route === "planning") setRoute(d.route);
        if (d.config?.width && d.config?.depth) setConfig(c => ({ ...c, width: d.config.width, depth: d.config.depth }));
        leadIdRef.current = rid;
        const stepsArr = d.route === "planning" ? PLANNING_STEPS : PD_STEPS;
        let target = "route";
        if (d.route === "planning") target = "planning";
        else if (d.route === "pd") target = ["site", "configurator", "quote"].includes(d.step) ? d.step : "pdscreen";
        setStepIdx(Math.max(stepsArr.indexOf(target), 1));
        toast.success("Welcome back — we've restored your details so you can carry on where you left off.");
      } catch { toast.error("That resume link is invalid or has expired."); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!pubSettings?.promo_active || !pubSettings?.promo_end_date) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pubSettings]);

  // recalc on changes (latest response wins — guards against out-of-order results)
  const calcSeq = useRef(0);
  useEffect(() => {
    if (stepIdx < 4) return;
    const t = setTimeout(async () => {
      const seq = ++calcSeq.current;
      try {
        const r = await api.post("/quote/calculate", { ...config, site, route });
        if (seq === calcSeq.current) setQuoteResult(r.data);
      } catch (e) { console.debug("Quote recalc skipped (non-blocking):", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [config, site, route, stepIdx]);

  const next = () => { setStepIdx(i => Math.min(steps.length - 1, i + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStepIdx(i => Math.max(0, i - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const [failInfo, setFailInfo] = useState(null);
  const openFail = async (reasons, msg = "") => {
    try { await api.post("/enquiry", { type: "quote-blocked", postcode, reasons, message: msg, payload: { route, propType, pdDepth, site, config } }); } catch (e) { console.debug("Blocked-quote enquiry not logged (non-blocking):", e); }
    setFailInfo({ reasons });
  };
  const submitEnquiry = async (reasons, msg = "") => {
    try {
      await api.post("/enquiry", { type: "quote-blocked", postcode, reasons, message: msg, payload: { route, propType, pdDepth, site, config } });
      toast.success("Enquiry sent. We'll be in touch within one working day.");
      nav("/");
    } catch { toast.error("Could not send enquiry."); }
  };

  const checkPostcode = async () => {
    const r = await api.post("/quote/postcode", {
      postcode,
      session_id: (typeof localStorage !== "undefined" && localStorage.getItem("de_chat_session")) || "",
      source: "quote",
    });
    setServiceable(r.data.serviceable);
    if (r.data.staff_bypass) toast.info("Out-of-area postcode allowed — staff override (you're signed in as admin).");
    if (r.data.serviceable) {
      // Contact already captured (staff prefill / resumed quote) — go straight through.
      // Otherwise stay on this step: the contact box below the postcode must be completed first.
      if (contact.name.trim() && contact.email.trim() && contact.phone.trim()) next();
      else setTimeout(() => document.querySelector('[data-testid="postcode-contact-box"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
    else {
      // Auto-launch Daisy with a tailored greeting so out-of-area visitors don't drop off.
      window.dispatchEvent(new CustomEvent("daisy:open", {
        detail: {
          source: "postcode-outside",
          postcode: (postcode || "").toUpperCase(),
          greeting: `Good news — although ${(postcode || "your postcode").toUpperCase()} is outside our instant-quote area, we can still cover your area on a bespoke basis (just not through the instant quote tool). If you'd like a member of our team to reach out with a tailored quote, could I take your name and email? A phone number and a couple of lines about your project would help too.`,
        },
      }));
    }
  };

  const pdCap = useMemo(() => !pubSettings ? 4 : (propType === "detached" ? pubSettings.pd_depth_detached : pubSettings.pd_depth_semi_terraced), [pubSettings, propType]);
  const paCap = useMemo(() => !pubSettings ? 6 : (propType === "detached" ? (pubSettings.pa_depth_detached || 8) : (pubSettings.pa_depth_semi_terraced || 6)), [pubSettings, propType]);
  const maxDepth = useMemo(() => {
    if (!pubSettings) return 4;
    if (route === "planning") return pubSettings.planning_max_depth;
    return paCap; // PD route may slide up to the Prior Approval allowance
  }, [pubSettings, route, paCap]);

  // Flag the site record when the chosen depth exceeds the PD limit (Prior Approval territory),
  // so the saved quote / booking carries the prior-approval status through to the office.
  useEffect(() => {
    if (!pubSettings || route !== "pd") return;
    const required = config.depth > pdCap + 1e-9;
    setSite(s => (s.prior_approval_required === required && s.prior_approved === (required ? priorApproved : ""))
      ? s : { ...s, prior_approval_required: required, prior_approved: required ? priorApproved : "" });
  }, [config.depth, pdCap, pubSettings, route, priorApproved]);

  const maxWidth = useMemo(() => {
    if (!pubSettings) return 6;
    return route === "planning" ? pubSettings.planning_max_width : pubSettings.pd_max_width;
  }, [pubSettings, route]);

  return (
    <main data-testid="quote-page" className="min-h-[80vh]">
      <PromoCountdown pubSettings={pubSettings} now={now} />
      {isStaff && (
        <div data-testid="quote-staff-mode-banner" className="bg-[#12263A] text-white text-xs px-6 md:px-12 lg:px-16 py-2 flex items-center gap-2 justify-center">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#C79A3B]" />
          <span>Staff mode — postcode &amp; contact gate skipped. Enter the client&apos;s details on the reveal step to save the quote.</span>
        </div>
      )}
      <StepTracker phaseIndex={PHASE_OF[step] ?? 0} />

      <h1 className="sr-only">Get your free single-storey rear extension quote — The Extension Works</h1>

      {step === "postcode" && (
        <PostcodeStep postcode={postcode} setPostcode={setPostcode} serviceable={serviceable}
          contact={contact} setContact={setContact} onContinue={next}
          onCheck={checkPostcode} onEnquiry={() => submitEnquiry(["postcode-outside"], `Postcode: ${postcode}`)} />
      )}

      {step === "route" && (
        <RouteStep onPick={(r) => { setRoute(r); next(); }} />
      )}

      {step === "pdscreen" && route === "pd" && (
        <PDScreen onPass={next} onFail={(reasons)=>openFail(reasons)} propType={propType} setPropType={setPropType} pdDepth={pdDepth} setPdDepth={setPdDepth} settings={pubSettings} pd={pd} setPd={setPd}
          pdCap={pdCap} paCap={paCap} priorApproved={priorApproved} setPriorApproved={setPriorApproved}/>
      )}

      {step === "planning" && (
        <PlanningQuoteStep
          postcode={postcode}
          planningRef={planningRef}
          setPlanningRef={setPlanningRef}
          settings={pubSettings}
          onProceed={({ quoteId, total, name, email, mobile }) => {
            setPlanningQuoteId(quoteId);
            setPlanningContact({ name, email, mobile });
            setQuoteResult({ total_inc_vat: total });
            next();
          }}
          onBack={back}
        />
      )}

      {step === "site" && (
        <SiteStep site={site} setSite={setSite} onContinue={(blocked)=>blocked? openFail(["rear-access-blocked"]) : next()} onBack={back}/>
      )}

      {step === "configurator" && (
        <ConfiguratorStep config={config} setConfig={setConfig} maxDepth={maxDepth} maxWidth={maxWidth} route={route} settings={pubSettings} quoteResult={quoteResult} addons={addons} onNext={next} onBack={back}
          pdCap={pdCap} paCap={paCap} priorApproved={priorApproved} setPriorApproved={setPriorApproved}/>
      )}

      {step === "quote" && (
        <QuoteRevealStep config={config} site={site} route={route} postcode={postcode} quoteResult={quoteResult}
          pdQuote={pdQuote} setPdQuote={setPdQuote} contact={contact} setContact={setContact} settings={pubSettings}
          propType={propType} pdDepth={pdDepth} editingQuoteId={editingQuoteId} enquiryId={enquiryId} linkBooking={linkBooking} onBack={back} onNext={next}
          isStaff={isStaff} adminNote={adminNote} onAdminNoteFlushed={() => { setAdminNote(""); clearAdminNoteDraft(); }} />
      )}

      {step === "book" && (
        <BookStep config={config} site={site} route={route} planningRef={planningRef} postcode={postcode} quoteResult={quoteResult} settings={pubSettings} planningQuoteId={planningQuoteId} planningContact={planningContact} pdQuote={pdQuote} contact={contact} propType={propType} pdDepth={pdDepth} onBack={back}/>
      )}
      <CriteriaFailModal failInfo={failInfo} onClose={() => setFailInfo(null)} onContact={() => nav("/contact")} />
      {isStaff && <StaffAdminNote value={adminNote} onChange={setAdminNote} />}
    </main>
  );
}
